import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { alpha, Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PolForumlaExprDialog } from '../../polFormula/PolFormulaExprDialog';

export const CalcProdResCompare = ( ) => {
  const { navState, navigateTo } = useNavState();
  const { hasRole } = usePrincipal();
  const { openDialog } = useDialog();
  const rsrc = useRsrc();
  const entityListRef = useRef();

  const [calcs, setCalcs] = useState([]);
  const [calcProdIds, setCalcProdIds] = useState([]);
  const [isOutlineOn, setIsOutlineOn] = useState(Boolean(navState?.isOutlineOn));
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  let calcProdCodes = { 0: '', 1: '' };
  const leftDataRef = useRef([]);
  const rightDataRef = useRef([]);


  useEffect(() => {
    if (!(Array.isArray(navState?.calcIds) && navState.calcIds.length > 0)) {
      return;
    }

    setCalcProdIds(navState.calcProdIds.filter(Boolean));
    const calcIds = navState.calcIds;
    const calcRowsReq = { expr: ExprUtils.whereParams('id', calcIds) };
    rsrc.rows('gp', 'CALC', calcRowsReq).then((calcRows) => {
      const sortedCalcs = new Array(calcIds.length);
      calcRows.forEach((calc) => {
        const index = calcIds.indexOf(calc.id);
        if (index >= 0) {
          sortedCalcs[index] = calc;
        }
      });
      setCalcs(sortedCalcs.filter(Boolean));

    });

  }, []);

  useEffect(() => {
    if (calcProdIds.length !== 2) {
      setShowAiSummary(false);
      return;
    }

    const rowsReq = { expr: ExprUtils.whereParams('id', calcProdIds) };
    rsrc.rows('gp', 'CALC_PROD', rowsReq).then((rows) => {
      const codeById = new Map(rows.map(row => [row.id, row.code]));
      const leftCode = codeById.get(calcProdIds[0]);
      const rightCode = codeById.get(calcProdIds[1]);
      calcProdCodes = { 0: leftCode, 1: rightCode };

      setShowAiSummary(leftCode === '59218260101' && rightCode === leftCode);
    });
  }, [calcProdIds]);

  useEffect(() => {
    if (!showAiSummary) {
      setAiLoading(false);
      return undefined;
    }

    setAiLoading(true);
    const timer = setTimeout(() => {
      setAiLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [showAiSummary]);

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    if (calcs.length !== 2) {
      return [];
    }
    const [leftCalc, rightCalc] = calcs;
    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!['name', 'code', 'type'].includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.code = {
      ...fieldToFieldDef.code,
      minWidth: 100,
      autoGrouping: isOutlineOn,
      rowGroup: isOutlineOn,
      precedence: 100
    };

    fieldToFieldDef.name = {
      ...fieldToFieldDef.name,
      minWidth: 280,
      precedence: 200
    };

    fieldToFieldDef.type = { 
      ...fieldToFieldDef.type,
      displayName: 'Type',
      field: 'type',
      width: 80,
      precedence: 300
    };

    fieldToFieldDef.calcA = {
      field: 'calcA',
      displayName: leftCalc?.name ?? 'Calculation A',
      precedence: 400,
      children: [
        {
          displayName: 'Value',
          field: 'value0',
          decimalPrecision: 6,
          valueGetter: (params) => toNumber(params.data?.value0),
          type: 'CALC_VALUE'
        },
        {
          displayName: 'Lines',
          field: 'lineCount0',
          type: 'NUMBER',
          width: 80
        }
      ]
    }

    fieldToFieldDef.calcB = {
      field: 'calcB',
      displayName: rightCalc?.name ?? 'Calculation B',
      precedence: 500,
      children: [
        {
          displayName: 'Value',
          field: 'value1',
          decimalPrecision: 6,
          valueGetter: (params) => toNumber(params.data?.value1),
          type: 'CALC_VALUE'
        },
        {
          displayName: 'Lines',
          field: 'lineCount1',
          type: 'NUMBER',
          width: 80
        }
      ]
    }

    fieldToFieldDef.comparison = {
      field: 'comparison',
      displayName: 'Comparison',
      marryChildren: true,
      precedence: 600,
      children: [
        {
          field: 'match',
          displayName: 'Match',
          width: 70,
          cellClass: 'ag-center-cell',
          type: 'BOOLEAN',
          valueGetter: (params) => {
            const value0 = toNumber(params.data?.value0);
            const value1 = toNumber(params.data?.value1);
            if (value0 == null && value1 == null) {
              return true;
            }
            return value0 != null && value1 != null && value0 === value1;
          },
        },
        {
          field: 'delta',
          displayName: 'Delta',
          width: 110,
          decimalPrecision: 6,
          valueGetter: (params) => {
            const value0 = toNumber(params.data?.value0);
            const value1 = toNumber(params.data?.value1);
            return value0 != null && value1 != null ? value1 - value0 : null;
          },
          type: 'CALC_VALUE',
        },
        {
          field: 'variance',
          displayName: 'Variance',
          width: 110,
          type: 'PERCENT',
          decimalPrecision: 2,
          valueGetter: (params) => {
            const value0 = toNumber(params.data?.value0);
            const value1 = toNumber(params.data?.value1);
            if (value0 == null || value0 === 0 || value1 == null) {
              return null;
            }
            return (value1 - value0) / value0;
          },
        },
      ]
    }
  }, [calcs, isOutlineOn]);




  const flatRowData = useCallback((setDataSource) => {
    if (calcs.length !== 2 || calcProdIds.length !== 2) {
      setDataSource([]);
      return;
    }

    const rowsReq0 = {
      expr: ExprUtils.whereParams('calcProdId', calcProdIds[0]),
      partition: calcs[0].partition,
      sortModel: [{ colId: 'id', sort: 'desc' }]
    };

    const rowsReq1 = {
      expr: ExprUtils.whereParams('calcProdId', calcProdIds[1]),
      partition: calcs[1].partition,
      sortModel: [{ colId: 'id', sort: 'desc' }]
    };

    Promise.all([
      rsrc.rows('gp', 'CALC_PROD_RES', rowsReq0),
      rsrc.rows('gp', 'CALC_PROD_RES', rowsReq1)
    ]).then(([rows0, rows1]) => {
      const rows1ByKey = new Map();
      rows1.forEach((row) => {
        const key = calcProdResKey(row);
        if (key) {
          rows1ByKey.set(key, row);
        }
      });

      const combined = rows0.map((row0) => {
        const key = calcProdResKey(row0);
        const row1 = rows1ByKey.get(key);
        return buildFlatRow(row0, row1);
      });

      setDataSource(combined);

      leftDataRef.current = rows0;
      rightDataRef.current = rows1;
    });
  }, [calcs, calcProdIds]);

  const clientSideRowData = useCallback((setDataSource) => {
    if (isOutlineOn) {
      const reqParam = {
        polId: calcs[0]?.polId, // Lead policy ID
        polIds: calcs.map((c) => c.polId),
        calcProdIds: calcProdIds
      };

      rsrc.data('gp', 'CALC_PROD_RES_DATA_PLUGIN', reqParam).then((res) => {
        setDataSource(res);
      });
      return;
    }

    flatRowData(setDataSource);
  }, [isOutlineOn, calcs, calcProdIds, flatRowData, rsrc]);

  const openPolFormulaExprDialog = (rowData) => {
    const data = {
      name: rowData.name,
      code: rowData.code,
      polId: calcs.map((c) => c.polId),
      module: 'gp',
    }
    openDialog(PolForumlaExprDialog, { data })
  }


  const contextMenuItems = (params) => {
    const contextMenuItems = [];

    const data = params.node.data;
    const trxApplicable = (data.type === 'AGG' || data.type === 'UNQAGG' || data.type === 'NOMAGG');
    const hasCalcProdResView = hasRole('GP_CALC_PROD_RES_VIEW');

    if (trxApplicable && hasCalcProdResView) {

      const leftRow = leftDataRef.current.find(row => row.id === data.id0);
      const leftReqParam = {
        code: 'AGG_CALC_TRX',
        data: leftRow,
        calc: calcs[0],
        ndc11: calcProdCodes[0],
        oppositeCalcId: calcs[1].id
      }

      const rightRow = rightDataRef.current.find(row => row.id === data.id1);
      const rightReqParam = {
        code: 'AGG_CALC_TRX',
        data: rightRow,
        calc: calcs[1],
        ndc11: calcProdCodes[1],
        oppositeCalcId: calcs[0].id
      }

      contextMenuItems.push({
        name: 'Transactions',
        icon: fas.faSignInAlt,
        subMenu: [{
          name: 'NDC11',
          icon: fas.faSignInAlt,
          action: () => navigateTo('gp/agg-lookup-viewer', { leftReqParam: { ...leftReqParam, ndcLevel: 'NDC11'}, rightReqParam: {...rightReqParam, ndcLevel: 'NDC11'}, entity: 'CALC' })
        }, {
          name: 'NDC11 & Linked Products',
          icon: fas.faSignInAlt,
          action: () => navigateTo('gp/agg-lookup-viewer', { leftReqParam: { ...leftReqParam, ndcLevel: 'NDC9'}, rightReqParam: {...rightReqParam, ndcLevel: 'NDC9'}, entity: 'CALC' })
        }]
      });
    }

    const polFormulaApplicable = !['FACTOR', 'REF', 'INFO'].includes(data.type);
    const hasPolFormulaView = hasRole('GP_POL_FORMULA_VIEW');
    if (polFormulaApplicable && hasPolFormulaView) {
      contextMenuItems.push({
        name: 'Formula Expression',
        icon: fas.faInfoCircle,
        action: () => openPolFormulaExprDialog(data)
      });
    }
    
    return contextMenuItems;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title icon={fas.faBalanceScale} title="Calculation Result Compare" />
        <ToggleButtonGroup
          value={isOutlineOn ? 'outline' : 'flat'}
          exclusive
          onChange={(e, value) => setIsOutlineOn(value === 'outline')}
          sx={{ height: 40, color: 'primary.main' }}>
          <ToggleButton value="outline" data-test-id="toggle-outline" sx={{ whiteSpace: 'nowrap' }}>
            Outline
          </ToggleButton>
          <ToggleButton value="flat" data-test-id="toggle-flat" sx={{ whiteSpace: 'nowrap' }}>
            Flat
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* This is a temporary box we adding to show what AI features could look like */}
      {showAiSummary && (
        <Box
          sx={(theme) => ({
            mt: 1,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : theme.palette.primary.light,
            padding: theme.spacing(2.5),
            pb: theme.spacing(8),
            minHeight: 140
          })}
        >
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>AI Summary</Typography>
          {aiLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              {/* <Typography variant="body1">...</Typography> */}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7 }}>
              The MAMP dropped in 2025M03 vs 2025M02 because <strong>Net Dollars fell while Net Units increased slightly.</strong>
              <br></br>
              <br></br>
              <strong>Net Dollars decreased by $19,140</strong>, driven mainly by:
              <br></br>
              &nbsp; &nbsp; <strong>Lower Direct Sales Amount</strong> (–$45K)
              <br></br>
              &nbsp; &nbsp; <strong>Lower Indirect Gov Sales</strong> (–$36K)
              <br></br>
              &nbsp; &nbsp; <strong>Higher Price Concessions</strong> (+$10K from chargebacks)
            </Typography>
          )}
        </Box>
      )}
      <EntityList
        ref={entityListRef}
        key={`${isOutlineOn}-${calcs[0]?.id}`} // Force remount when calcProdIds or isOutlineOn changes to reset grid state
        module="gp"
        entity="CALC_PROD_RES"
        showTitle={false}
        clientSideRowData={clientSideRowData}
        augFieldDefs={augFieldDefs}
        gridOptions={{
          treeData: isOutlineOn,
          defaultExpandLevel: 0,
          getContextMenuItems: contextMenuItems,
        }}
      />
    </Box>
  );
};

const calcProdResKey = (row) => {
  if (!row) {
    return null;
  }
  return row.code ?? null;
};

const buildFlatRow = (row0, row1) => {
  const base = {
    name: row0?.name ?? row1?.name,
    code: row0?.code ?? row1?.code,
    type: row0?.type ?? row1?.type,
    value0: row0?.value,
    lineCount0: row0?.lineCount,
    polFormulaId0: row0?.polFormulaId,
    incDataSrcCode0: row0?.incDataSrcCode,
    incDataSrcId0: row0?.incDataSrcId,
    trxSrcCode0: row0?.trxSrcCode,
    dateBasis0: row0?.dateBasis,
    trxSelPeriod0: row0?.trxSelPeriod,
    calcProdId0: row0?.calcProdId,
    id0: row0?.id
  };

  if (!row1) {
    return base;
  }

  return {
    ...base,
    value1: row1.value,
    lineCount1: row1.lineCount,
    polFormulaId1: row1.polFormulaId,
    incDataSrcCode1: row1.incDataSrcCode,
    incDataSrcId1: row1.incDataSrcId,
    trxSrcCode1: row1.trxSrcCode,
    dateBasis1: row1.dateBasis,
    trxSelPeriod1: row1.trxSelPeriod,
    calcProdId1: row1.calcProdId,
    id1: row1.id
  };
};

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};


